import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { PaymentResult, PaymentResultProps } from '@entities/payment';
import SimpleLoader from '../../../../../SimpleLoader';

interface PaymentResultFlowProps extends Omit<PaymentResultProps, 'isOpen'> {
	isLoading?: boolean;
	onMount?: () => void;
	onFlowStart?: () => Promise<boolean>;
	onFlowAbort?: () => void;
}

const PaymentResultFlow: React.FC<PaymentResultFlowProps> = ({
	result,
	isLoading,
	successAdditionalBanner,
	onMount,
	onFlowStart,
	onFlowAbort,
	onClick,
	onBackToOrder,
	onClose
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const landingStartedRef = useRef(false);

	useEffect(() => {
		if (landingStartedRef.current) {
			return;
		}

		landingStartedRef.current = true;

		onMount?.();
	}, [onMount]);

	useEffect(() => {
		if (!onFlowStart) {
			return undefined;
		}

		let isCancelled = false;

		(async () => {
			const isFlowSuccessful = await onFlowStart();

			if (!isCancelled && isFlowSuccessful) {
				setIsModalOpen(true);
			}
		})();

		return () => {
			isCancelled = true;
			onFlowAbort?.();
		};
	}, [onFlowStart, onFlowAbort]);

	const handleClick: PaymentResultProps['onClick'] = useCallback(
		(...args) => {
			setIsModalOpen(false);
			onClick?.(...args);
		},
		[onClick]
	);

	const handleBackToOrder = useCallback(() => {
		setIsModalOpen(false);
		onBackToOrder?.();
	}, [onBackToOrder]);

	const handleClose = useCallback(() => {
		setIsModalOpen(false);
		onClose?.();
	}, [onClose]);

	return (
		<>
			{isLoading && <SimpleLoader />}

			<PaymentResult
				result={result}
				isOpen={isModalOpen}
				successAdditionalBanner={successAdditionalBanner}
				onClick={onClick ? handleClick : undefined}
				onClose={onClose ? handleClose : undefined}
				onBackToOrder={onBackToOrder ? handleBackToOrder : undefined}
			/>
		</>
	);
};

export default PaymentResultFlow;
